import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { isFulfilled, isRejected } from "@reduxjs/toolkit";
import { addDays, format, parseISO, startOfDay } from "date-fns";
import { useEffect, useState } from "react";
import { Controller, FieldValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  epochToFormatDate,
  getTimeFromEpoch,
  isDateBefore,
} from "../../../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import CommonTextField from "../../../../../common/components/functional/commonTextField/CommonTextField";
import AutocompleteChip from "../../../../../common/components/functional/customAutocomplete/AutocompleteChip";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import isEmpty from "../../../../../common/helpers/isEmpty";
import { isoFormat } from "../../../../../common/helpers/formatDateTime";
import { AutocompleteType } from "../../../../../common/types/autoComplete";
import { fetchCustomerCostCentersByOrgIds } from "../../../../customer/customerCostCentersSlice";
import { fetchCustomerUsersByIds } from "../../../../customer/customerUsersSlice";
import { fetchWorkLocationById } from "../../../../customer/customerWorkLocationsSlice";
import { CustomerUser } from "../../../../customer/types/queries";
import { AssignmentById, CostCenter } from "../../../../talents/talentAPI";
import {
  changeAssignmentCostCenters,
  endAssignment,
  updateAssignmentRange,
} from "../../../../talents/talentsSlice";
import { AssignmentRow } from "../AssignmentRow";
import {
  fetchOrderRowsByBundleId,
  selectOrderTitleByBundleId,
} from "../../../../orders/orderRowsDetailedSlice";
import NewTabLinkButton from "../../../../../common/components/functional/links/NewTabLinkButton";
import { routes } from "../../../../../app/paths";
import isEqual from "../../../../../common/helpers/isEqual";
import { EventPayload } from "../../../../../common/types/Event";

/**
 * Run assignment update steps sequentially. Each step receives the current
 * version and returns the new version from its response (or undefined to keep it).
 * Stops on first rejection. Use for commands that must run in order due to versioning.
 */
async function runSequentialUpdates(
  initialVersion: number,
  steps: Array<(version: number) => Promise<EventPayload<any> | undefined>>,
): Promise<void> {
  let version = initialVersion;
  for (const run of steps) {
    const nextVersion = await run(version).then((p) => p?.version);
    if (nextVersion !== undefined) version = nextVersion;
  }
}

/**
 * Builds a versioned step: adds version to payload, dispatches thunk, returns new version.
 * Limited to thunks whose fulfilled payload is a single object with .version (not an array).
 * @param dispatch - The dispatch function to use.
 * @param payload - The payload to use.
 * @param thunk - The thunk to use.
 * @returns A function that returns the new version.
 */
function makeVersionedStep<P extends { version: number }>(
  dispatch: (thunk: unknown) => Promise<EventPayload<any>>,
  payload: Omit<P, "version">,
  thunk: (arg: P) => unknown,
): (version: number) => Promise<EventPayload<any>> {
  return (version: number) => {
    const arg = { ...payload, version } as P;
    return dispatch(thunk(arg)).then((action) => {
      if (isRejected(action)) throw action.error;
      if (!isFulfilled(action)) return undefined;
      const p = action.payload;
      return p;
    });
  };
}

interface Props {
  assignment: AssignmentById;
  canEdit: boolean;
  /** When true, assignment end date can be edited (admin only). */
  canEditAssignmentEnd?: boolean;
  isShift?: boolean;
}

const getDateTimeValue = (
  value?: EpochTimeStamp | null,
  hideTime?: boolean,
) => {
  const dateValue = value ?? undefined;
  return (
    <Box display="flex" gap="1rem">
      <Typography>{epochToFormatDate(dateValue)}</Typography>
      {hideTime ? null : (
        <Typography>{getTimeFromEpoch(dateValue)}</Typography>
      )}
    </Box>
  );
};

const isValidEpochDate = (value: unknown): value is number =>
  typeof value === "number" &&
  Number.isFinite(value) &&
  !Number.isNaN(new Date(value).getTime());

/**
 * How much longer admin can make the assignment than the employment. Keep in sync with the backend value at
 * `srv-working/src/main/kotlin/fi/eezycore/working/assignment/AssignmentRangePolicy.kt`
 */
const ADMIN_ASSIGNMENT_END_AFTER_EMPLOYMENT_GRACE_DAYS = 2;

const dateFromIsoDate = (value?: string | null) => {
  if (!value) return null;
  const date = startOfDay(parseISO(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const isDayAfter = (date: Date, date2: Date) =>
  startOfDay(date).getTime() > startOfDay(date2).getTime();

const BasicDetails = ({
  assignment,
  canEdit,
  canEditAssignmentEnd = false,
  isShift,
}: Props) => {
  const canEditAssignmentRange = canEditAssignmentEnd && !isShift;
  const canEditEndDate = canEditAssignmentRange;
  const [edit, setEdit] = useState(false);
  const [assignmentEndOpen, setAssignmentEndOpen] = useState(
    !isValidEpochDate(assignment.details.end),
  );
  const [costCenterOptions, setCostCenterOptions] = useState<CostCenter[]>([]);
  const [customerContactPersons, setCustomerContactPersons] = useState<
    CustomerUser[]
  >([]);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const translatedWorkTitles = useAppSelector(
    (state) => state.localized.workTitle,
  );

  const orderTitleByBundleId = useAppSelector((state) =>
    selectOrderTitleByBundleId(state, assignment.bundleId),
  );

  const orderTitle =
    orderTitleByBundleId === undefined
      ? t("order:noOrderForAssignment") // no order
      : orderTitleByBundleId.trim().length === 0
        ? t("order:newOrder") // legacy order without title
        : orderTitleByBundleId; // order name

  const orderLink = assignment.bundleId
    ? routes.orderEdit(assignment.bundleId)
    : "";

  useEffect(() => {
    if (assignment.bundleId) {
      dispatch(fetchOrderRowsByBundleId({ id: assignment.bundleId }));
    }
  }, [assignment.bundleId, dispatch]);

  useEffect(() => {
    if (assignment.details.workLocationId && assignment.details.customerOrgId) {
      // get worklocation contact persons
      dispatch(
        fetchWorkLocationById({
          workLocationId: assignment.details.workLocationId,
        }),
      ).then((action) => {
        if (isFulfilled(action)) {
          if (
            !isEmpty(action.payload.talentCustomerContactPersons) &&
            action.payload.talentCustomerContactPersons
          ) {
            const ids = action.payload.talentCustomerContactPersons.map(
              ({ id }) => id,
            );
            // get contact person details by ids
            dispatch(fetchCustomerUsersByIds(ids)).then((action) => {
              if (isFulfilled(action)) {
                // set customer contact person options
                setCustomerContactPersons(action.payload.data);
              }
            });
          }

          if (action.payload.customerCCIds) {
            const params = {
              customerOrgIds: assignment.details.customerOrgId,
              customerCCIds: action.payload.customerCCIds.join(","),
            };
            // get cost center details by ids
            dispatch(fetchCustomerCostCentersByOrgIds(params)).then(
              (action) => {
                if (isFulfilled(action)) {
                  const costCenterOptions = action.payload
                    .filter((costCenter) => costCenter.active)
                    .map((costCenter) => ({
                      id: costCenter.customerCCId,
                      name: costCenter.name,
                    }));
                  // set cost center options
                  setCostCenterOptions(costCenterOptions);
                }
              },
            );
          }
        }
      });
    }
  }, [
    dispatch,
    assignment.details.workLocationId,
    assignment.details.customerOrgId,
  ]);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { isDirty, errors },
  } = useForm({
    defaultValues: {
      ...assignment.details,
      end: assignment.details.end ?? null,
      contactPersons: customerContactPersons || [],
    },
    mode: "onChange",
  });

  const startValue = watch("start");
  const endValue = watch("end");
  const employmentStartDate = dateFromIsoDate(assignment.employmentStartDate);
  const employmentEndDate = dateFromIsoDate(assignment.employmentEndDate);
  const assignmentEndAllowedUntil = employmentEndDate
    ? addDays(
        employmentEndDate,
        ADMIN_ASSIGNMENT_END_AFTER_EMPLOYMENT_GRACE_DAYS,
      )
    : null;

  useEffect(() => {
    reset({
      ...assignment.details,
      end: assignment.details.end ?? null,
      contactPersons: customerContactPersons || [],
    });
  }, [assignment.details, reset, customerContactPersons]);

  useEffect(() => {
    setAssignmentEndOpen(!isValidEpochDate(assignment.details.end));
  }, [assignment.details.end]);

  const onSave = (fields: FieldValues) => {
    // TODO bit of a messy stuff here
    const ccs = fields.custCostCenters as unknown as CostCenter[];
    const costCentersChanged =
      !isEmpty(fields.custCostCenters) &&
      !isEqual(
        ccs.map(({ id }) => id),
        assignment.details.custCostCenters.map(({ id }) => id),
      );
    const endChanged = canEditEndDate && fields.end !== assignment.details.end;
    const endDateCleared = endChanged && fields.end == null;
    const startChanged =
      canEditAssignmentRange &&
      fields.start != null &&
      fields.start !== assignment.details.start &&
      Number.isFinite(fields.start);
    const endPayload =
      endChanged && isValidEpochDate(fields.end)
        ? {
            assignmentId: assignment.assignmentId,
            endDate: format(new Date(fields.end - 1), isoFormat),
            reason: t("talentProfile:assignmentEndReasonSetByAdmin"),
          }
        : null;
    const rangePayload =
      startChanged || endDateCleared
        ? {
            assignmentId: assignment.assignmentId,
            startDate: format(new Date(fields.start), isoFormat),
            endDate: isValidEpochDate(fields.end)
              ? format(new Date(fields.end - 1), isoFormat)
              : null,
            reason: t("talentProfile:assignmentEndReasonSetByAdmin"),
          }
        : null;

    const steps = [
      ...(costCentersChanged
        ? [
            makeVersionedStep(
              dispatch,
              {
                assignmentId: assignment.assignmentId,
                custCostCenters: fields.custCostCenters,
              },
              changeAssignmentCostCenters,
            ),
          ]
        : []),
      ...(rangePayload
        ? [makeVersionedStep(dispatch, rangePayload, updateAssignmentRange)]
        : endPayload
          ? [makeVersionedStep(dispatch, endPayload, endAssignment)]
          : []),
    ];

    // this runs the steps sequentially and sets the edit to false if all steps are successful
    void runSequentialUpdates(assignment.details.version, steps).then(
      () => setEdit(false),
      (r) => {
        console.warn("Failed to update assignment", r);
      },
    );
  };

  const onEdit = () => {
    if (edit) {
      handleSubmit(onSave)();
      return;
    }

    setEdit(true);
  };

  const onCancel = () => {
    setEdit(false);
    setAssignmentEndOpen(!isValidEpochDate(assignment.details.end));
    reset();
  };

  const renderCustomerContactPersons = (value: Partial<CustomerUser>) => {
    // If phone number is available and it's longer than 4 characters (country code), show it
    if (value.account?.phone && value.account?.phone.length > 4) {
      return (
        <Typography key={value.id}>
          {`${value?.account?.firstName} ${value?.account?.lastName}, ${value?.account?.phone}`}
        </Typography>
      );
    }

    return (
      <Typography key={value.id}>
        {`${value?.account?.firstName} ${value?.account?.lastName}`}
      </Typography>
    );
  };

  const validateCostCenters = (newValue: AutocompleteType[]) => {
    if (isEmpty(newValue)) return [];
    // short employment: only one cost center allowed
    // long employment: multiple cost centers allowed
    const isShift = assignment.details.isShift;
    const validatedCcs = isShift ? [newValue[newValue.length - 1]] : newValue;
    return validatedCcs;
  };

  const saveDisabled = !isDirty || !isEmpty(errors);

  return (
    <FormSection
      title={t("common:basicInfo")}
      edit={edit}
      onEdit={onEdit}
      onCancel={onCancel}
      readOnly={!canEdit}
      saveDisabled={saveDisabled}
    >
      <AssignmentRow
        label={t("common:customerOrganizationName")}
        field={
          <Controller
            name="customerOrgName"
            control={control}
            render={({ field }) => <Typography>{field.value}</Typography>}
          />
        }
      />
      <AssignmentRow
        label={t("talentProfile:workLocationName")}
        field={
          <Controller
            name="workLocationName"
            control={control}
            render={({ field }) => <Typography>{field.value}</Typography>}
          />
        }
      />
      <AssignmentRow
        label={t("talentProfile:workTitleSingle")}
        field={
          <Controller
            name="workTitleId"
            control={control}
            render={({ field }) => (
              <Typography>
                {translatedWorkTitles[field.value] || field.value}
              </Typography>
            )}
          />
        }
      />
      <AssignmentRow
        label={t("talentProfile:costCenterNames")}
        field={
          <Controller
            name="custCostCenters"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange } }) => (
              <Autocomplete
                multiple
                fullWidth
                disabled={!edit}
                options={costCenterOptions.sort((a, b) =>
                  a.name.localeCompare(b.name),
                )}
                value={value}
                disableClearable
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <AutocompleteChip
                        key={key}
                        {...tagProps}
                        label={option.name}
                        readOnly={!edit}
                      />
                    );
                  })
                }
                onChange={(_: any, newValue: CostCenter[]) => {
                  const validatedCostCenters = validateCostCenters(newValue);
                  return onChange(validatedCostCenters);
                }}
                renderInput={(params) => (
                  <CommonTextField
                    {...params}
                    name="custCostCenters"
                    sx={{ maxWidth: "300px" }}
                    error={!!errors.custCostCenters}
                  />
                )}
              />
            )}
          />
        }
      />
      <AssignmentRow
        gridContainerProps={{
          alignItems: "flex-start",
        }}
        label={t("talentProfile:workContactPersons")}
        field={
          <Controller
            name="contactPersons"
            control={control}
            render={({ field }) => (
              <Box>
                {field.value.map((contact) =>
                  renderCustomerContactPersons(contact),
                )}
              </Box>
            )}
          />
        }
      />
      <AssignmentRow
        label={t("talentProfile:assignmentStartDate")}
        field={
          <Controller
            name="start"
            control={control}
            rules={{
              validate: (value) => {
                if (!value || !canEditAssignmentRange) return true;
                if (
                  employmentStartDate &&
                  isDateBefore(new Date(value), employmentStartDate)
                ) {
                  return t(
                    "errors:invalid_assignment_start_date_before_employment",
                  );
                }
                if (
                  isValidEpochDate(endValue) &&
                  isDateBefore(new Date(endValue - 1), new Date(value))
                ) {
                  return t("errors:invalid_assignment_end_must_be_after_start");
                }
                return true;
              },
            }}
            render={({ field }) =>
              edit && canEditAssignmentRange ? (
                <DesktopDatePicker
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) =>
                    field.onChange(date ? date.getTime() : field.value)
                  }
                  {...(employmentStartDate
                    ? {
                        minDate: employmentStartDate,
                      }
                    : {})}
                  {...(isValidEpochDate(endValue)
                    ? {
                        maxDate: new Date(endValue - 1),
                      }
                    : {})}
                  format="dd.MM.yyyy"
                  slotProps={{
                    textField: {
                      size: "small",
                      error: !!errors.start,
                      fullWidth: true,
                      sx: { maxWidth: "300px" },
                    },
                  }}
                />
              ) : (
                getDateTimeValue(
                  field.value,
                  // For targeted assignments, show time
                  assignment.isTargetedShift ? false : !isShift,
                )
              )
            }
          />
        }
      />
      <AssignmentRow
        label={t("talentProfile:assignmentEndDate")}
        field={
          <Controller
            name="end"
            control={control}
            rules={{
              validate: (value) => {
                if (!value || !canEditEndDate) return true;
                if (
                  isValidEpochDate(startValue) &&
                  isDateBefore(new Date(value), new Date(startValue))
                ) {
                  return t("errors:invalid_assignment_end_must_be_after_start");
                }
                if (
                  assignmentEndAllowedUntil &&
                  isDayAfter(new Date(value - 1), assignmentEndAllowedUntil)
                ) {
                  return t(
                    "errors:invalid_assignment_end_date_after_employment",
                  );
                }
                return true;
              },
            }}
            render={({ field }) =>
              edit && canEditEndDate ? (
                <Box>
                  <DesktopDatePicker
                    value={
                      isValidEpochDate(field.value)
                        ? new Date(field.value - 1)
                        : null
                    }
                    onChange={(date) => {
                      field.onChange(date ? date.getTime() + 1000 : null);
                      setAssignmentEndOpen(!date);
                    }}
                    disabled={!employmentEndDate && assignmentEndOpen}
                    {...(isValidEpochDate(startValue)
                      ? {
                          minDate: new Date(startValue),
                        }
                      : {})}
                    {...(assignmentEndAllowedUntil
                      ? {
                          maxDate: assignmentEndAllowedUntil,
                        }
                      : {})}
                    format="dd.MM.yyyy"
                    slotProps={{
                      textField: {
                        size: "small",
                        error: !!errors.end,
                        fullWidth: true,
                        sx: { maxWidth: "300px" },
                      },
                    }}
                  />
                  {!employmentEndDate && (
                    <FormControlLabel
                      sx={{ ml: 1 }}
                      control={
                        <Checkbox
                          checked={assignmentEndOpen}
                          onChange={(_, checked) => {
                            setAssignmentEndOpen(checked);
                            if (checked) field.onChange(null);
                          }}
                        />
                      }
                      label={t("talentProfile:assignmentContinuesIndefinitely")}
                    />
                  )}
                </Box>
              ) : (
                getDateTimeValue(
                  // Subtract 1 second to show the correct date for long assignments
                  !(isShift || assignment.isTargetedShift) && field.value ? field.value - 1 : field.value,
                  // For targeted assignments, show time
                  assignment.isTargetedShift ? false : !isShift,
                )
              )
            }
          />
        }
      />
      <AssignmentRow
        label={t("order:order")}
        gridContainerProps={{ maxHeight: "32px", alignItems: "center" }}
        field={
          <NewTabLinkButton
            text={orderTitle}
            to={orderLink}
            buttonSx={{
              fontSize: "14px",
              padding: "4px 8px",
              maxHeight: "24px",
              marginLeft: "-8px",
              marginTop: "-4px",
            }}
          />
        }
      />
    </FormSection>
  );
};

export default BasicDetails;
